import React, { useMemo, useState } from "react";
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from "react-redux";
import { setShowPaymentPlans, toggleDialog } from "../../../../redux/Slices/Global/globalSlice";
import UpgradeDialog from "./UpgradeDialog";
import { useProfileContext } from "../../../Contexts/ProfileContext";

// Keep your original feature order
const FEATURE_ORDER = [
    "3d", "clock", "layouts", "weather", "voice_call", "remote_control", "device_preview",
    "animated_layouts", "rss_feed", "add_users", "ai_access", "media_type", "prayer_app",
    "multiple_layer", "content_preview", "music_list_access", "playlist_capacity",
    "interactive_action", "full_player_offline", "live_meeting_access", "published_as_grouping",
    "media_storage_capacity", "advance_content_schedule", "upload_once_content_capacity",
    "ad_marketplace", "match_scheduler", "smart_ad_scheduler", "hardware_control",
];

function MembershipPlansTable() {
    const { t } = useTranslation();
    const { packages = [], companyDetails, openDialog } = useSelector((s) => s.global);
    const [selectedPeriod, setSelectedPeriod] = useState("one_month"); // default to yearly like the screenshot
    const [selectedPlan, setSelectedPlan] = useState();
    const dispatch = useDispatch();
    const { setIsUpgrade } = useProfileContext();

    // Helpers
    const periodLabel =
        selectedPeriod === "one_month" ? t('payment.period_monthly') :
            selectedPeriod === "six_months" ? t('payment.period_six_months') : t('payment.period_yearly');

    const humanize = (key) =>
        String(key).replace(/_/g, " ").replace(/\b\w/g, (m) => m.toUpperCase());

    const fmtValue = (key, val) => {
        if (Array.isArray(val)) return val.join(", ");
        if (val === true || val === false) return null;
        if (typeof val === "number" && key.includes("capacity"))
            return `${val >= 10000 ? t('payment.unlimited') : val}${key.includes("media_storage") ? " GB" : ""}`;
        return val;
    };

    const currentPkgId = companyDetails?.package?.id || null;

    const currentPkg = useMemo(
        () => packages.find((p) => p.id === currentPkgId),
        [packages, currentPkgId]
    );

    const currentIsFree = currentPkg?.is_free === 1;

    // Finished state (current or previous subscription)
    const isFinished = useMemo(() => {
        const sub = companyDetails?.subscription;
        if (typeof companyDetails?.isFinished === "boolean") return companyDetails.isFinished;
        if (!sub) return false;
        const inactive = sub.status !== "ACTIVE";
        const pastEnd = sub.end_date ? new Date(sub.end_date) < new Date() : false;
        return inactive || pastEnd;
    }, [companyDetails]);

    // Which packages to show
    const visiblePackages = useMemo(() => {
        if (!packages?.length) return [];
        return packages.filter((p) => (currentIsFree ? true : p.is_free !== 1));
    }, [packages, currentIsFree]);

    // Normalize for selected period
    const normalized = useMemo(() => {
        const periodKey = selectedPeriod.toUpperCase(); // ONE_MONTH | SIX_MONTHS | ONE_YEAR
        return visiblePackages.map((pkg) => {
            const priceRow = pkg.prices?.find((pr) => pr.period === periodKey);
            return {
                ...pkg,
                price: priceRow?.price ?? pkg.basic_price ?? 0,
                periodKey,
            };
        });
    }, [visiblePackages, selectedPeriod]);

    const ctaFor = (planId, price) => {
        if (planId === currentPkgId) {
            return isFinished && price !== 0
                ? { label: t('payment.prev_plan_renew'), disabled: false, tone: "prev" }
                : isFinished && price === 0
                    ? { label: t('payment.prev_plan_switch'), disabled: true, tone: "prev" }
                    : { label: t('payment.current_plan'), disabled: true, tone: "current" };
        }
        return { label: t('payment.choose_this_plan'), disabled: false, tone: "switch" };
    };

    const handleChoose = (plan) => {
        const oldPrice = companyDetails?.package?.prices?.[0]?.price ?? 0;
        const newPrice = plan.prices?.[0]?.price ?? 0;
        setIsUpgrade(!(oldPrice >= newPrice));
        setSelectedPlan(plan);
        dispatch(toggleDialog("upgrade"));
    };

    // Simple icons
    const Check = () => <span aria-hidden>✔</span>;
    const Cross = () => <span aria-hidden>✕</span>;

    return (
        <>
            <div className="my-3 mx-3 pb-1 fs-3 d-flex justify-content-between align-items-center">
                <div>{t('payment.membership_plans')}</div>
                <div className="fs-5 cursor-pointer" onClick={() => dispatch(setShowPaymentPlans(false))}>X</div>
            </div>

            {/* Billing period toggle */}
            <div className="d-flex justify-content-center mb-3">
                <div className="d-inline-flex align-items-center gap-3">
                    <button
                        className={`btn btn-sm ${selectedPeriod === "one_month" ? "btn-primary" : "btn-outline-secondary"}`}
                        onClick={() => setSelectedPeriod("one_month")}
                    >
                        {t('payment.period_monthly')}
                    </button>
                    <button
                        className={`btn btn-sm ${selectedPeriod === "six_months" ? "btn-primary" : "btn-outline-secondary"}`}
                        onClick={() => setSelectedPeriod("six_months")}
                    >
                        {t('payment.period_six_months')}
                    </button>
                    <button
                        className={`btn btn-sm ${selectedPeriod === "one_year" ? "btn-primary" : "btn-outline-secondary"}`}
                        onClick={() => setSelectedPeriod("one_year")}
                    >
                        {t('payment.period_yearly')}
                    </button>
                </div>
            </div>

            {/* Scroll container so wide tables look good on mobile */}
            <div className="w-100" style={{ overflowX: "auto" }}>
                <table className="table table-bordered align-middle text-center" style={{ minWidth: 920 }}>
                    <thead className="">
                        <tr className="bg-light">
                            <th className="text-center " style={{ width: 280 }}>
                                <div className="fw-bold flex justify-center items-center">{t('payment.features_header')}</div>
                            </th>

                            {normalized.map((plan) => {
                                const cta = ctaFor(plan.id, plan.price);
                                const isCurrent = plan.id === currentPkgId;

                                return (
                                    <th key={plan.id} style={{ minWidth: 200 }}>
                                        <div className="d-flex flex-column align-items-center gap-2 py-2">
                                            <div className="fw-bold">{plan.title}</div>
                                            <div className="fs-5">
                                                <sup style={{ position: "relative", right: "3px" }}>$</sup>
                                                <span className="fw-bold">{Number(plan.price).toFixed(2)}</span>
                                            </div>
                                            <div className="text-muted small">{periodLabel}</div>

                                            {/* Status badge like screenshot */}
                                            <div>
                                                {isCurrent && !isFinished && (
                                                    <span className="badge bg-success-subtle text-success">{t('payment.badge_current')}</span>
                                                )}
                                                {isCurrent && isFinished && (
                                                    <span className="badge bg-warning-subtle text-warning">{t('payment.badge_expired')}</span>
                                                )}
                                                {!isCurrent && (
                                                    <span className="badge bg-secondary-subtle text-secondary">{t('payment.badge_available')}</span>
                                                )}
                                            </div>

                                            <button
                                                className={`btn btn-sm ${cta.tone === "current" ? "btn-outline-secondary" : "btn-primary"} w-100`}
                                                disabled={cta.disabled}
                                                onClick={() => handleChoose(plan)}
                                            >
                                                {cta.label}
                                            </button>
                                        </div>
                                    </th>
                                );
                            })}
                        </tr>
                    </thead>

                    <tbody>
                        {FEATURE_ORDER.map((key) => (
                            <tr key={key}>
                                {/* Feature name */}
                                <th scope="row" className="text-center">
                                    {humanize(key)}
                                </th>

                                {/* One cell per plan */}
                                {normalized.map((plan) => {
                                    const v = plan.features?.[key.toLowerCase()];
                                    const extra = fmtValue(key.toLowerCase(), v);
                                    const isBool = typeof v === "boolean";

                                    let cell;
                                    if (isBool) {
                                        cell = v ? <Check /> : <Cross />;
                                    } else if (extra) {
                                        cell = <span className="small">{extra}</span>;
                                    } else if (v == null) {
                                        cell = <span className="text-muted">—</span>;
                                    } else {
                                        cell = String(v);
                                    }

                                    // Green checks like the screenshot
                                    const ok = v === true || (!!extra && v !== false);

                                    return (
                                        <td key={`${plan.id}-${key}`} className={ok ? " " : ""}>
                                            {cell}
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {openDialog.upgrade && <UpgradeDialog plan={selectedPlan} />}
        </>
    );
}

export default MembershipPlansTable;
